import React, { useState } from 'react';
import { shopApi } from '../../utils/shopApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { FileText, Printer, RefreshCw } from 'lucide-react';

const rwf = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n || 0);

/**
 * Z-Report — end-of-day counter close-out summary.
 * Cashiers see their own totals; admins see all cashiers.
 */
export default function ZReportDialog({ open, onOpenChange, isAdmin }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await shopApi.get(`/shop/pos/z-report`, { params: { date } });
      setReport(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Z-report');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const printReport = () => window.print();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="z-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-primary" />Z-Report · Counter close-out</DialogTitle>
          <DialogDescription>End-of-day POS summary. Print at counter close and hand to supervisor.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3 mb-4 print:hidden">
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} className="h-9" data-testid="z-report-date" />
          </div>
          <Button size="sm" onClick={load} disabled={loading} data-testid="z-report-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={printReport} data-testid="z-report-print">
            <Printer className="w-4 h-4 mr-1" />Print
          </Button>
        </div>

        {loading && <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>}

        {!loading && report && (
          <div className="space-y-5 text-sm">
            <div className="text-center border-b border-dashed pb-3">
              <div className="text-lg font-serif font-bold">Bison Shop · Z-Report</div>
              <div className="text-xs text-muted-foreground">Day: {report.date} · Generated {new Date(report.generated_at).toLocaleTimeString()}</div>
              <div className="text-xs text-muted-foreground">Scope: {report.is_admin_view ? (report.cashier_scope === 'all' ? 'All cashiers' : `Cashier ${report.cashier_scope}`) : 'Your sales only'}</div>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="z-report-totals">
              <StatBox label="Transactions" value={report.totals.count} />
              <StatBox label="Gross" value={`RWF ${rwf(report.totals.gross)}`} />
              <StatBox label="Discounts" value={`- RWF ${rwf(report.totals.discount)}`} tone="warn" />
              <StatBox label="Net" value={`RWF ${rwf(report.totals.net)}`} tone="good" />
            </div>

            {/* Payment breakdown */}
            <div>
              <h4 className="font-semibold mb-2">Payment split</h4>
              <div className="grid grid-cols-3 gap-3" data-testid="z-report-payments">
                {['cash', 'momo', 'card'].map((k) => {
                  const p = report.by_payment[k] || { count: 0, amount: 0 };
                  return (
                    <div key={k} className="p-3 rounded-lg border border-border bg-muted/30">
                      <div className="text-xs uppercase text-muted-foreground">{k}</div>
                      <div className="font-semibold">RWF {rwf(p.amount)}</div>
                      <div className="text-xs text-muted-foreground">{p.count} sale{p.count === 1 ? '' : 's'}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Per-cashier */}
            {report.per_cashier.length > 1 && (
              <div>
                <h4 className="font-semibold mb-2">Per-cashier totals</h4>
                <div className="border border-border rounded-lg overflow-hidden" data-testid="z-report-per-cashier">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-left">
                      <tr>
                        <th className="px-3 py-2">Cashier</th>
                        <th className="px-3 py-2 text-right">Sales</th>
                        <th className="px-3 py-2 text-right">Cash</th>
                        <th className="px-3 py-2 text-right">MoMo</th>
                        <th className="px-3 py-2 text-right">Card</th>
                        <th className="px-3 py-2 text-right">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.per_cashier.map((c) => (
                        <tr key={c.cashier_id} className="border-t border-border" data-testid={`z-report-cashier-${c.cashier_id}`}>
                          <td className="px-3 py-2 font-medium">{c.cashier_name}</td>
                          <td className="px-3 py-2 text-right">{c.count}</td>
                          <td className="px-3 py-2 text-right">RWF {rwf(c.by_method.cash)}</td>
                          <td className="px-3 py-2 text-right">RWF {rwf(c.by_method.momo)}</td>
                          <td className="px-3 py-2 text-right">RWF {rwf(c.by_method.card)}</td>
                          <td className="px-3 py-2 text-right font-semibold">RWF {rwf(c.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Items sold */}
            <div>
              <h4 className="font-semibold mb-2">Items sold ({report.items_sold.length})</h4>
              <div className="border border-border rounded-lg overflow-hidden max-h-64 overflow-y-auto" data-testid="z-report-items">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 text-left sticky top-0">
                    <tr>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.items_sold.length === 0 && (
                      <tr><td colSpan={3} className="px-3 py-4 text-center text-muted-foreground">No sales for this date.</td></tr>
                    )}
                    {report.items_sold.map((r) => (
                      <tr key={r.product_id} className="border-t border-border">
                        <td className="px-3 py-2">{r.product_name}</td>
                        <td className="px-3 py-2 text-right">{r.quantity} {r.unit}</td>
                        <td className="px-3 py-2 text-right font-medium">RWF {rwf(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-between items-center text-sm border-t pt-3">
              <div><span className="text-muted-foreground">EBM invoices raised:</span> <strong>{report.ebm_invoice_count}</strong></div>
              <div className="text-xs text-muted-foreground">— End of report —</div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatBox({ label, value, tone }) {
  const toneCls = tone === 'good' ? 'text-emerald-700 bg-emerald-50' : tone === 'warn' ? 'text-amber-700 bg-amber-50' : 'text-foreground bg-muted/40';
  return (
    <div className={`p-3 rounded-lg border border-border ${toneCls}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value}</div>
    </div>
  );
}
